// Hit Testing & Geometry Math for Chart Drawings

export function distanceToSegment(px, py, x1, y1, x2, y2) {
  const l2 = (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1);
  if (l2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * (x2 - x1)), py - (y1 + t * (y2 - y1)));
}

export function findDrawingAtCoordinate(screenX, screenY, drawings, chart, candleSeries) {
  if (!drawings || drawings.length === 0 || !chart || !candleSeries) return null;

  const timeScale = chart.timeScale();
  const threshold = 8; // Pixel distance threshold

  for (let i = drawings.length - 1; i >= 0; i--) {
    const item = drawings[i];

    if (item.type === "trendline") {
      if (!item.p1 || !item.p2) continue;
      const x1 = timeScale.timeToCoordinate(item.p1.time);
      const y1 = candleSeries.priceToCoordinate(item.p1.price);
      const x2 = timeScale.timeToCoordinate(item.p2.time);
      const y2 = candleSeries.priceToCoordinate(item.p2.price);
      if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
        const dist = distanceToSegment(screenX, screenY, x1, y1, x2, y2);
        if (dist <= threshold) return item;
      }
    } else if (item.type === "horizontal") {
      if (item.price === undefined || item.price === null) continue;
      const y = candleSeries.priceToCoordinate(item.price);
      if (y !== null && Math.abs(screenY - y) <= threshold) {
        return item;
      }
    } else if (item.type === "rectangle") {
      if (!item.p1 || !item.p2) continue;
      const x1 = timeScale.timeToCoordinate(item.p1.time);
      const y1 = candleSeries.priceToCoordinate(item.p1.price);
      const x2 = timeScale.timeToCoordinate(item.p2.time);
      const y2 = candleSeries.priceToCoordinate(item.p2.price);
      if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
        const minX = Math.min(x1, x2);
        const maxX = Math.max(x1, x2);
        const minY = Math.min(y1, y2);
        const maxY = Math.max(y1, y2);

        // Check if clicked near border or inside
        const inside = screenX >= minX - 4 && screenX <= maxX + 4 && screenY >= minY - 4 && screenY <= maxY + 4;
        if (inside) return item;
      }
    } else if (item.type === "fibonacci") {
      if (!item.p1 || !item.p2) continue;
      const x1 = timeScale.timeToCoordinate(item.p1.time);
      const y1 = candleSeries.priceToCoordinate(item.p1.price);
      const x2 = timeScale.timeToCoordinate(item.p2.time);
      const y2 = candleSeries.priceToCoordinate(item.p2.price);
      if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
        const minX = Math.min(x1, x2) - 10;
        const maxX = Math.max(x1, x2) + 90;
        const minY = Math.min(y1, y2) - 10;
        const maxY = Math.max(y1, y2) + 10;
        if (screenX >= minX && screenX <= maxX && screenY >= minY && screenY <= maxY) {
          return item;
        }
      }
    } else if (item.type === "text") {
      if (!item.p) continue;
      const x = timeScale.timeToCoordinate(item.p.time);
      const y = candleSeries.priceToCoordinate(item.p.price);
      if (x !== null && y !== null) {
        if (screenX >= x - 5 && screenX <= x + 120 && screenY >= y - 25 && screenY <= y + 10) {
          return item;
        }
      }
    }
  }

  return null;
}

// Master Hit Test for Drag Handles & Drawing Bodies
export function findDrawingHitTarget(screenX, screenY, drawings, selectedDrawingId, chart, candleSeries) {
  if (!chart || !candleSeries) return { type: "none" };

  const timeScale = chart.timeScale();
  const HANDLE_RADIUS = 12; // 12px hit radius for easy grabbing on corners

  const selectedDrawing = (drawings || []).find((d) => d.id === selectedDrawingId);

  // 1. Check selected drawing handles FIRST
  if (selectedDrawing) {
    if (selectedDrawing.type === "rectangle" && selectedDrawing.p1 && selectedDrawing.p2) {
      const x1 = timeScale.timeToCoordinate(selectedDrawing.p1.time);
      const y1 = candleSeries.priceToCoordinate(selectedDrawing.p1.price);
      const x2 = timeScale.timeToCoordinate(selectedDrawing.p2.time);
      const y2 = candleSeries.priceToCoordinate(selectedDrawing.p2.price);

      if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
        const handles = [
          { id: "p1", x: x1, y: y1, cursor: "nwse-resize" },
          { id: "p2", x: x2, y: y2, cursor: "nwse-resize" },
          { id: "p1_x2y1", x: x2, y: y1, cursor: "nesw-resize" },
          { id: "p1_x1y2", x: x1, y: y2, cursor: "nesw-resize" },
        ];

        for (const h of handles) {
          if (Math.hypot(screenX - h.x, screenY - h.y) <= HANDLE_RADIUS) {
            return { type: "handle", handleId: h.id, drawing: selectedDrawing, cursor: h.cursor };
          }
        }
      }
    } else if ((selectedDrawing.type === "trendline" || selectedDrawing.type === "fibonacci") && selectedDrawing.p1 && selectedDrawing.p2) {
      const x1 = timeScale.timeToCoordinate(selectedDrawing.p1.time);
      const y1 = candleSeries.priceToCoordinate(selectedDrawing.p1.price);
      const x2 = timeScale.timeToCoordinate(selectedDrawing.p2.time);
      const y2 = candleSeries.priceToCoordinate(selectedDrawing.p2.price);

      if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
        if (Math.hypot(screenX - x1, screenY - y1) <= HANDLE_RADIUS) {
          return { type: "handle", handleId: "p1", drawing: selectedDrawing, cursor: "pointer" };
        }
        if (Math.hypot(screenX - x2, screenY - y2) <= HANDLE_RADIUS) {
          return { type: "handle", handleId: "p2", cursor: "pointer", drawing: selectedDrawing };
        }
      }
    } else if (selectedDrawing.type === "horizontal" && selectedDrawing.price !== undefined) {
      const y = candleSeries.priceToCoordinate(selectedDrawing.price);
      if (y !== null && Math.abs(screenY - y) <= HANDLE_RADIUS) {
        return { type: "handle", handleId: "price", drawing: selectedDrawing, cursor: "ns-resize" };
      }
    } else if (selectedDrawing.type === "text" && selectedDrawing.p) {
      const x = timeScale.timeToCoordinate(selectedDrawing.p.time);
      const y = candleSeries.priceToCoordinate(selectedDrawing.p.price);
      if (x !== null && y !== null && Math.hypot(screenX - x, screenY - y) <= HANDLE_RADIUS) {
        return { type: "handle", handleId: "p", drawing: selectedDrawing, cursor: "move" };
      }
    }

    // 2. Check selected drawing BODY
    const bodyHit = isPointInsideDrawing(screenX, screenY, selectedDrawing, timeScale, candleSeries);
    if (bodyHit) {
      return { type: "body", drawing: selectedDrawing, cursor: "move" };
    }
  }

  // 3. Check any other drawing (to select it)
  const hitOther = findDrawingAtCoordinate(screenX, screenY, drawings, chart, candleSeries);
  if (hitOther) {
    return { type: "select", drawing: hitOther, cursor: "pointer" };
  }

  return { type: "none", cursor: "crosshair" };
}

function isPointInsideDrawing(screenX, screenY, item, timeScale, candleSeries) {
  if (!item) return false;
  if (item.type === "rectangle" && item.p1 && item.p2) {
    const x1 = timeScale.timeToCoordinate(item.p1.time);
    const y1 = candleSeries.priceToCoordinate(item.p1.price);
    const x2 = timeScale.timeToCoordinate(item.p2.time);
    const y2 = candleSeries.priceToCoordinate(item.p2.price);
    if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);
      return screenX >= minX && screenX <= maxX && screenY >= minY && screenY <= maxY;
    }
  } else if ((item.type === "trendline" || item.type === "fibonacci") && item.p1 && item.p2) {
    const x1 = timeScale.timeToCoordinate(item.p1.time);
    const y1 = candleSeries.priceToCoordinate(item.p1.price);
    const x2 = timeScale.timeToCoordinate(item.p2.time);
    const y2 = candleSeries.priceToCoordinate(item.p2.price);
    if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
      return distanceToSegment(screenX, screenY, x1, y1, x2, y2) <= 10;
    }
  } else if (item.type === "horizontal" && item.price !== undefined) {
    const y = candleSeries.priceToCoordinate(item.price);
    if (y !== null) return Math.abs(screenY - y) <= 8;
  } else if (item.type === "text" && item.p) {
    const x = timeScale.timeToCoordinate(item.p.time);
    const y = candleSeries.priceToCoordinate(item.p.price);
    if (x !== null && y !== null) {
      return screenX >= x - 5 && screenX <= x + 120 && screenY >= y - 25 && screenY <= y + 10;
    }
  }
  return false;
}
